import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Table, PageHeader, Modal, FormField, ConfirmDialog } from '../../../components/UI';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const empty = { noc_name: '', noc_code: '', description: '', final_dept_id: '', related_dept_id: '', is_active: 1 };

export default function NocTypeMaster() {
  const [items, setItems]           = useState([]);
  const [depts, setDepts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(empty);
  const [templateFile, setTemplateFile] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [docModal, setDocModal]     = useState(null);
  const [docDemands, setDocDemands] = useState([]);
  const [newDoc, setNewDoc]         = useState({ document_name: '', is_mandatory: 1, sort_order: 0 });

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/masters/noc-types'), api.get('/masters/departments')])
      .then(([n, d]) => { setItems(n.noc_types || []); setDepts(d.departments || []); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setTemplateFile(null); setEditing(null); setModal(true); };

  const openEdit = (item) => {
    setForm({ ...item, final_dept_id: item.final_dept_id || '', related_dept_id: item.related_dept_id || '' });
    setTemplateFile(null);
    setEditing(item);
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (templateFile) fd.append('template_file', templateFile);

      if (editing) {
        await api.put('/masters/noc-types/' + editing.id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('NOC Type updated');
      } else {
        await api.post('/masters/noc-types', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('NOC Type created');
      }
      setModal(false);
      load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await api.delete('/masters/noc-types/' + deleteModal.id);
    toast.success('Deactivated'); setDeleteModal(null); load();
  };

  const openDocModal = async (item) => {
    setDocModal(item);
    const r = await api.get('/masters/document-demands/' + item.id);
    setDocDemands(r.demands || []);
  };

  const addDocDemand = async () => {
    await api.post('/masters/document-demands', { ...newDoc, noc_type_id: docModal.id });
    const r = await api.get('/masters/document-demands/' + docModal.id);
    setDocDemands(r.demands || []);
    setNewDoc({ document_name: '', is_mandatory: 1, sort_order: 0 });
    toast.success('Document demand added');
  };

  const deleteDocDemand = async (id) => {
    await api.delete('/masters/document-demands/' + id);
    setDocDemands(d => d.filter(x => x.id !== id));
    toast.success('Removed');
  };

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const columns = [
    { key: 'noc_code', label: 'Code' },
    { key: 'noc_name', label: 'NOC Name' },
    { key: 'related_dept', label: 'Related Dept' },
    { key: 'final_dept',   label: 'Final Dept' },
    {
      key: 'template_file', label: 'Template',
      render: r => r.template_file
        ? <a href={API_BASE + r.template_file} target="_blank" rel="noopener noreferrer" download
            className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded hover:bg-green-100">
            📥 Download
          </a>
        : <span className="text-xs text-gray-400">—</span>,
    },
    {
      key: 'is_active', label: 'Status',
      render: r => <span className={r.is_active ? 'text-green-600 font-medium' : 'text-red-500'}>{r.is_active ? 'Active' : 'Inactive'}</span>,
    },
    {
      key: 'actions', label: 'Actions',
      render: r => (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => openDocModal(r)} className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-1 rounded hover:bg-blue-100">📋 Docs</button>
          <button onClick={() => openEdit(r)} className="text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 px-2 py-1 rounded hover:bg-yellow-100">✏️ Edit</button>
          <button onClick={() => setDeleteModal(r)} className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-100">🗑️</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="NOC Type Master" subtitle="Manage all NOC types, department mapping and templates"
        actions={<button className="btn-primary" onClick={openAdd}>+ Add NOC Type</button>} />

      <div className="card overflow-hidden p-0">
        <Table columns={columns} data={items} loading={loading} />
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit NOC Type' : 'Add NOC Type'} size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="NOC Name" required>
              <input className="form-input" value={form.noc_name} onChange={e => f('noc_name', e.target.value)} required />
            </FormField>
            <FormField label="NOC Code" required>
              <input className="form-input" value={form.noc_code} onChange={e => f('noc_code', e.target.value.toUpperCase())} required />
            </FormField>
            <FormField label="Related Department">
              <select className="form-select" value={form.related_dept_id} onChange={e => f('related_dept_id', e.target.value)}>
                <option value="">Select Department</option>
                {depts.filter(d => d.dept_type === 'related').map(d => <option key={d.id} value={d.id}>{d.dept_name}</option>)}
              </select>
            </FormField>
            <FormField label="Final Approval Department">
              <select className="form-select" value={form.final_dept_id} onChange={e => f('final_dept_id', e.target.value)}>
                <option value="">Select Department</option>
                {depts.filter(d => d.dept_type === 'final_approval').map(d => <option key={d.id} value={d.id}>{d.dept_name}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select className="form-select" value={form.is_active} onChange={e => f('is_active', e.target.value)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </FormField>
          </div>

          <FormField label="Description">
            <textarea className="form-input" rows={3} value={form.description} onChange={e => f('description', e.target.value)} />
          </FormField>

          {/* ── Template Upload Section ── */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">📄</span>
              <div>
                <p className="text-sm font-semibold text-blue-800">Template Document</p>
                <p className="text-xs text-blue-600">
                  Upload a template (PDF/Word) that citizens must download, fill &amp; re-upload.
                  Ideal for <strong>Dhara 80</strong> and similar NOC types.
                </p>
              </div>
            </div>

            {editing?.template_file && (
              <div className="flex items-center gap-3 p-3 bg-white border border-blue-200 rounded-lg">
                <span className="text-2xl">📎</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700">Current Template</p>
                  <p className="text-xs text-gray-400 truncate">{editing.template_file.split('/').pop()}</p>
                </div>
                <a href={API_BASE + editing.template_file} target="_blank" rel="noopener noreferrer" download
                  className="flex-shrink-0 text-xs bg-green-100 text-green-700 border border-green-300 px-3 py-1.5 rounded hover:bg-green-200 font-medium">
                  📥 Download
                </a>
              </div>
            )}

            <div>
              <label className="form-label">{editing?.template_file ? 'Replace Template (optional)' : 'Upload Template File'}</label>
              <input type="file" accept=".pdf,.doc,.docx" className="form-input"
                onChange={e => setTemplateFile(e.target.files[0] || null)} />
              <p className="text-xs text-gray-400 mt-1">Accepted formats: PDF, DOC, DOCX</p>
            </div>

            {templateFile && (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-green-600">✅</span>
                <span className="text-xs text-green-700 font-medium">{templateFile.name}</span>
                <span className="text-xs text-green-500">({(templateFile.size / 1024).toFixed(1)} KB)</span>
                <button type="button" onClick={() => setTemplateFile(null)} className="ml-auto text-red-400 hover:text-red-600 text-xs">✕ Remove</button>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save NOC Type'}</button>
          </div>
        </form>
      </Modal>

      {/* ── Document Demands Modal ── */}
      <Modal isOpen={!!docModal} onClose={() => setDocModal(null)} title={'Document Demands — ' + (docModal?.noc_name || '')} size="lg">
        <div className="space-y-4">
          <div className="space-y-2">
            {docDemands.length === 0
              ? <p className="text-sm text-gray-400 text-center py-4">No document demands configured</p>
              : docDemands.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div>
                    <p className="text-sm font-medium">{d.document_name}</p>
                    <p className="text-xs text-gray-400">{d.is_mandatory ? '🔴 Mandatory' : '⚪ Optional'} • Order: {d.sort_order}</p>
                  </div>
                  <button onClick={() => deleteDocDemand(d.id)} className="text-red-400 hover:text-red-600 text-sm">🗑️</button>
                </div>
              ))
            }
          </div>
          <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-sm font-semibold text-gray-700 mb-3">Add New Document Demand</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input className="form-input" placeholder="Document Name" value={newDoc.document_name}
                onChange={e => setNewDoc(p => ({ ...p, document_name: e.target.value }))} />
              <select className="form-select" value={newDoc.is_mandatory}
                onChange={e => setNewDoc(p => ({ ...p, is_mandatory: e.target.value }))}>
                <option value={1}>Mandatory</option>
                <option value={0}>Optional</option>
              </select>
              <input type="number" className="form-input" placeholder="Sort Order" value={newDoc.sort_order}
                onChange={e => setNewDoc(p => ({ ...p, sort_order: e.target.value }))} />
            </div>
            <button onClick={addDocDemand} className="btn-primary mt-3 text-sm" disabled={!newDoc.document_name}>+ Add Document</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteModal} onClose={() => setDeleteModal(null)} onConfirm={handleDelete}
        title="Deactivate NOC Type" message={'Are you sure you want to deactivate "' + (deleteModal?.noc_name || '') + '"?'}
        confirmLabel="Deactivate" danger />
    </div>
  );
}